import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import { CUSTOM_TIMEFRAME, PRESET_TIMEFRAMES, earliestExamDay, type GoalDraft } from 'modules/studyPlan';
import { ChoiceCard } from './ChoiceCard';
import { examKeys, timeframeKeys } from './options';

interface DatesStepProps {
  drafts: GoalDraft[];
  onChange: (draft: GoalDraft) => void;
}

const TIMEFRAMES = [...PRESET_TIMEFRAMES, CUSTOM_TIMEFRAME];

/** When each exam is: a timeframe, or a day after today. Exams rarely fall on the same day, so each has its own. */
export function DatesStep({ drafts, onChange }: DatesStepProps) {
  const { t } = useTranslation();
  const earliest = earliestExamDay();
  return (
    <div className="space-y-8">
      {drafts.map((draft) => {
        const exam = t(examKeys(draft.exam).label, { defaultValue: draft.exam });
        const id = `dates-${draft.exam}`;
        return (
          <section key={draft.exam} aria-labelledby={id} className="space-y-3">
            {drafts.length > 1 && <h2 id={id} className="text-lg font-extrabold tracking-tight text-neutral-900 dark:text-white">{exam}</h2>}
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2" aria-labelledby={drafts.length > 1 ? id : undefined}>
              {TIMEFRAMES.map((timeframe) => {
                const keys = timeframeKeys(timeframe);
                return (
                  <ChoiceCard
                    key={timeframe}
                    label={t(keys.label)}
                    sub={keys.sub && t(keys.sub)}
                    selected={draft.timeframe === timeframe}
                    onSelect={() => onChange({ ...draft, timeframe })}
                  />
                );
              })}
            </div>
            {draft.timeframe === CUSTOM_TIMEFRAME && (
              <label className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                <Calendar className="h-4 w-4 text-neutral-400" aria-hidden />
                <span className="sr-only">{t('learnerOb.dates_exam_date', { exam })}</span>
                <input
                  type="date"
                  value={draft.examDate}
                  min={earliest}
                  onChange={(event) => onChange({ ...draft, examDate: event.target.value })}
                  className="flex-1 bg-transparent text-base font-semibold outline-none focus-visible:!outline-none dark:text-white md:text-sm"
                />
              </label>
            )}
          </section>
        );
      })}
    </div>
  );
}

export default DatesStep;
